import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, BehaviorSubject } from 'rxjs';


type ThankYouRouteMatchType = 'exact' | 'variant' | 'default';

interface ThankYouRouteRule {
  provider: string;
  routeKeys: string[];
  allowVariants?: boolean;
}

interface ThankYouRoutesConfig {
  thankYouRoutes: ThankYouRouteRule[];
  defaultProvider: string;
  journeyStage: string;
}

interface ResolvedThankYouRoute {
  provider: string;
  journeyStage: string;
  routeKey: string;
  routeMatch: string;
  routeMatchType: ThankYouRouteMatchType;
}

@Injectable({
  providedIn: 'root'
})
export class ThankYouRouteService {
  private readonly configUrl = '/assets/config/thank-you-routes.json';
  private configPromise?: Promise<ThankYouRoutesConfig>;

  private thankYouPageRouteSubject = new BehaviorSubject<any>({});
  thankYouPageRoute$ = this.thankYouPageRouteSubject.asObservable();

  private readonly fallbackConfig: ThankYouRoutesConfig = {
    thankYouRoutes: [
      {
        provider: 'tandem',
        routeKeys: ['CHL', 'CHS', 'CHRF', 'CHWN', 'CHG'],
      },
      {
        provider: 'mao',
        routeKeys: ['MAOE', 'MAOET', 'MAO'],
      },
      {
        provider: 'upsell',
        routeKeys: ['UPSELL'],
        allowVariants: true,
      },
    ],
    defaultProvider: 'internal',
    journeyStage: 'incremental_monetization_thank_you',
  };

  constructor(private http: HttpClient) {}

  async resolve(routeKey: string | null | undefined): Promise<ResolvedThankYouRoute> {
    const config = await this.loadConfig();
    const resolvedRouteKey = String(routeKey || '').trim();
    const normalizedRouteKey = this.normalize(routeKey);

    for (const route of config.thankYouRoutes || []) {
      const routeKeys = route.routeKeys || [];

      for (const configuredKey of routeKeys) {
        const normalizedConfiguredKey = this.normalize(configuredKey);
        const isExactMatch = normalizedRouteKey === normalizedConfiguredKey;
        const isVariantMatch = Boolean(route.allowVariants)
          && normalizedRouteKey.startsWith(`${normalizedConfiguredKey}_`);

        if (normalizedConfiguredKey && (isExactMatch || isVariantMatch)) {
          return {
            provider: route.provider,
            journeyStage: config.journeyStage || this.fallbackConfig.journeyStage,
            routeKey: resolvedRouteKey,
            routeMatch: configuredKey,
            routeMatchType: isVariantMatch ? 'variant' : 'exact',
          };
        }
      }
    }

    return {
      provider: config.defaultProvider || this.fallbackConfig.defaultProvider,
      journeyStage: config.journeyStage || this.fallbackConfig.journeyStage,
      routeKey: resolvedRouteKey,
      routeMatch: '',
      routeMatchType: 'default',
    };
  }

  private loadConfig(): Promise<ThankYouRoutesConfig> {
    if (!this.configPromise) {
      const url = `${this.configUrl}?ts=${Date.now()}`;
      this.configPromise = firstValueFrom(this.http.get<ThankYouRoutesConfig>(url))
        .then(config => this.sanitizeConfig(config))
        .catch(() => this.fallbackConfig);
    }

    return this.configPromise;
  }

  private sanitizeConfig(config: ThankYouRoutesConfig | null | undefined): ThankYouRoutesConfig {
    if (!config || !Array.isArray(config.thankYouRoutes) || !config.defaultProvider || !config.journeyStage) {
      return this.fallbackConfig;
    }

    return config;
  }

  private normalize(value: string | null | undefined): string {
    return String(value || '').trim().toUpperCase();
  }

  sendThankyouPageRoute(thanksSubId: any): void {
    this.thankYouPageRouteSubject.next(thanksSubId);
  }

}
